import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Mail } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="px-4 py-4 flex items-center gap-4">
        <Link to="/login" className="text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">FP</span>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            {sent ? (
              <div data-testid="forgot-password-success" className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">
                  Check Your Email
                </h1>
                <p className="text-slate-600 mb-6">
                  If an account exists for {email}, you'll receive a password reset link shortly.
                </p>
                <Link to="/login">
                  <Button
                    data-testid="back-to-login-btn"
                    className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl"
                  >
                    Back to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <h1
                  data-testid="forgot-password-title"
                  className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-2"
                >
                  Forgot Password?
                </h1>
                <p className="text-slate-600 mb-6">
                  Enter your email and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div
                      data-testid="forgot-password-error"
                      className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm"
                    >
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email
                    </Label>
                    <Input
                      id="email"
                      data-testid="forgot-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="forgot-submit-btn"
                    disabled={loading}
                    className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl shadow-md shadow-blue-700/20"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                        Sending...
                      </span>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                </form>

                <p className="mt-6 text-center text-slate-600">
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    data-testid="back-to-login-link"
                    className="text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    Log in
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
